import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Text,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Grid,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import baseUrl from 'Base_Url/baseUrl';
import axiosInstance from 'axiosInstance';

// For Graphs
import {
  LineChart,
  Legend,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';

// For excel downloads
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { ReactComponent as PdfDownload } from '../../../assets/icons/PDF.svg';
import { ReactComponent as XlDownload } from '../../../assets/icons/XL Icon.svg';

const AnalyticsDashboard = () => {
  const userRole = localStorage.getItem('role');
  const [userPermissions, setUserPermissions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabEnergyConsumption, setactiveTabEnergyConsumption] =
    useState(0);
  const [selectedPid, setSelectedPid] = useState('');
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');

  const [startDateEnergyCons, setStartDateEnergyCons] = useState('');

  const [dailyAbstraction, setDailyAbstraction] = useState([]);
  const [analyticsLogs, setAnalyticsLogs] = useState([]);
  const [energyConsumptionGraph, setenergyConsumptionGraph] = useState([]);

  const [avg_vll, setAvgVll] = useState([]);
  const [current, setCurrent] = useState([]);
  const [powerFactor, setPowerFactor] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [voltage, setVoltage] = useState([]);
  const [kwh, setKwh] = useState([]);
  const [kw, setKw] = useState([]);

  const [deviceList, setDeviceList] = useState([]);

  const Token = localStorage.getItem('token');
  const toast = useToast();

  const [canMonitor, setCanMonitor] = useState(true);

  const chartRef0 = useRef();
  const chartRef1 = useRef();
  const chartRef2 = useRef();

  // Refs for energy consumption graphs
  const ecChartRef0 = useRef();
  const ecChartRef1 = useRef();
  const ecChartRef2 = useRef();
  const ecChartRef3 = useRef();
  const ecChartRef4 = useRef();
  const ecChartRef5 = useRef();
  const ecChartRef6 = useRef();

  const allTabs = [
    {
      label: 'Water Abstraction for Borewell',
      key: 'borewell_abstraction',
    },
    {
      label: 'Water Abstraction for Plant',
      key: 'plant_abstraction',
    },
    {
      label: 'Borewell Operation Log',
      key: 'operation_log',
    },
    {
      label: 'Energy Consumption',
      key: 'energy_consumption',
    },
    {
      label: 'Alert Log',
      key: 'alert_log',
    },
  ];

  // hide these for baseuser
  const restrictedTabs = ['operation_log', 'energy_consumption', 'alert_log'];

  const visibleTabs =
    userRole === 'baseuser'
      ? allTabs.filter((tab) => !restrictedTabs.includes(tab.key))
      : allTabs;

  const abstractionChartData = dailyAbstraction
    .map((item) => ({
      date: moment(item.date).format('DD-MMM'),
      Unit: item.dailyConsumption || 0,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const borewellOperationChartData = Object.values(
    analyticsLogs
      .filter((item) => item.type === 'operation') // ✅ Only include operation logs
      .reduce((acc, item) => {
        const formattedDate = moment(item.date).format('DD-MMM');
        const key = `${item.pid}_${formattedDate}`;

        // Parse "0h 0m 11s" → total seconds
        const match = item.duration.match(/(\d+)h\s+(\d+)m\s+(\d+)s/);
        const hours = parseInt(match?.[1] || 0, 10);
        const minutes = parseInt(match?.[2] || 0, 10);
        const seconds = parseInt(match?.[3] || 0, 10);
        const durationSeconds = hours * 3600 + minutes * 60 + seconds;

        if (!acc[key]) {
          acc[key] = {
            pid: item.pid,
            date: formattedDate,
            value: 0,
          };
        }

        acc[key].value += durationSeconds;
        return acc;
      }, {}),
  )
    .sort((a, b) => moment(a.date, 'DD-MMM') - moment(b.date, 'DD-MMM'))
    .map((item) => {
      const hours = Math.floor(item.value / 3600);
      const minutes = Math.floor((item.value % 3600) / 60);
      const seconds = item.value % 60;
      const label = `${hours}h ${minutes}m ${seconds}s`;

      return {
        ...item,
        label, // human-readable duration
      };
    });

  const handleDownloadGraphPDF = async () => {
    let PdfHeadline;
    let chartElement;
    let fileName;
    switch (activeTab) {
      case 0:
        PdfHeadline = 'Borewell Water Abstraction (Daily Report)';
        chartElement = chartRef0.current;
        fileName = `Borewell_Water_Abstraction_Report_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 1:
        PdfHeadline = 'Plant Water Abstraction (Daily Report)';
        chartElement = chartRef1.current;
        fileName = `Plant_Water_Abstraction_Report.pdf`;
        break;

      case 2:
        PdfHeadline = 'Borewell Operation Logs (Daily Report)';
        chartElement = chartRef2.current;
        fileName = `Borewell_Operation_Logs_Report_${selectedPid || 'N/A'}.pdf`;
        break;

      default:
        PdfHeadline = 'N/A';
    }
    // const chartElement = chartRef.current;

    if (!chartElement) {
      return;
    }

    // Convert chart to canvas
    const canvas = await html2canvas(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // ✅ Add title and details at the top
    const marginTop = 10;
    pdf.setFontSize(16);

    pdf.text(PdfHeadline, pdfWidth / 2, marginTop, {
      align: 'center',
    });

    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);

    const generatedDate = new Date().toLocaleString();

    pdf.text(`Product ID: ${selectedPid || 'Complete Plant'}`, 15, 25);
    pdf.text(
      `Date Range: ${startDate || 'N/A'} to ${stopDate || 'N/A'}`,
      15,
      32,
    );
    pdf.text(`Generated On: ${generatedDate}`, 15, 39);

    // Add chart image slightly below the details
    const chartYPosition = 45;
    pdf.addImage(imgData, 'PNG', 10, chartYPosition, pdfWidth - 20, pdfHeight);

    pdf.save(fileName);
  };

  const handleDownloadGraphPDFForEC = async () => {
    let PdfHeadline;
    let chartElement;
    let fileName;
    switch (activeTabEnergyConsumption) {
      case 0:
        PdfHeadline = 'Energy consumption Report VLL';
        chartElement = ecChartRef0.current;
        fileName = `Energy_consumption_Report_VLL_${selectedPid || 'N/A'}.pdf`;
        break;

      case 1:
        PdfHeadline = 'Energy consumption Report Current';
        chartElement = ecChartRef1.current;
        fileName = `Energy_consumption_Report_Current.pdf`;
        break;

      case 2:
        PdfHeadline = 'Energy consumption Report Power Factor';
        chartElement = ecChartRef2.current;
        fileName = `Energy_consumption_Report_Power_Factor_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 3:
        PdfHeadline = 'Energy consumption Report Frequency';
        chartElement = ecChartRef3.current;
        fileName = `Energy_consumption_Report_Frequency_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 4:
        PdfHeadline = 'Energy consumption Report Voltage';
        chartElement = ecChartRef4.current;
        fileName = `Energy_consumption_Report_Voltage_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 5:
        PdfHeadline = 'Energy consumption Report KWH';
        chartElement = ecChartRef5.current;
        fileName = `Energy_consumption_Report_Kwh_${selectedPid || 'N/A'}.pdf`;
        break;

      case 6:
        PdfHeadline = 'Energy consumption Report KW';
        chartElement = ecChartRef6.current;
        fileName = `Energy_consumption_Report_Kw_${selectedPid || 'N/A'}.pdf`;
        break;

      default:
        PdfHeadline = 'N/A';
    }
    // const chartElement = chartRef.current;

    if (!chartElement) {
      return;
    }

    // Convert chart to canvas
    const canvas = await html2canvas(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // ✅ Add title and details at the top
    const marginTop = 10;
    pdf.setFontSize(16);

    pdf.text(PdfHeadline, pdfWidth / 2, marginTop, {
      align: 'center',
    });

    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);

    const generatedDate = new Date().toLocaleString();

    pdf.text(`Product ID: ${selectedPid || 'Complete Plant'}`, 15, 25);
    pdf.text(`For Date: ${startDateEnergyCons || 'N/A'}`, 15, 32);
    pdf.text(`Generated On: ${generatedDate}`, 15, 39);

    // Add chart image slightly below the details
    const chartYPosition = 45;
    pdf.addImage(imgData, 'PNG', 10, chartYPosition, pdfWidth - 20, pdfHeight);

    pdf.save(fileName);
  };

  const downloadExcelForBorewellAbstraction = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = new Date().toLocaleString();

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push([
      'Date',
      'Product ID',
      'Opening Timestamp',
      'Opening Value (Totalizer in m³)',
      'Closing Timestamp',
      'Closing Value (Totalizer in m³)',
      'Water Abstraction (unit in m³)',
    ]);

    // Table rows
    dailyAbstraction.forEach((item) => {
      wsData.push([
        new Date(item.date).toLocaleDateString('en-GB'), // DD-MM-YYYY
        item.pid,
        new Date(item.openingDate).toLocaleString('en-GB', {
          hour12: true,
          timeZone: 'UTC',
        }),
        item.openingTotalizer,
        new Date(item.closingDate).toLocaleString('en-GB', {
          hour12: true,
          timeZone: 'UTC',
        }),
        item.closingTotalizer,
        item.dailyConsumption,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(wb, `Daily_Abstraction_Report_${productId || 'Plant'}.xlsx`);
  };

   const downloadExcelForPlantAbstraction = () => {
     // Example variables
     const companyName = localStorage.getItem('companyName');
     // const productName = 'G-Block';
     const productId = selectedPid;
     const downloadDate = new Date().toLocaleString();

     // Prepare worksheet data
     const wsData = [];

     // Top info rows
     wsData.push([`Company Name: ${companyName}`]);
     // wsData.push([`Product Name: ${productName}`]);
     wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
     wsData.push([`Date of Download: ${downloadDate}`]);
     wsData.push([]); // empty row before table

     // Table headers
     wsData.push([
       'Date',
       'Opening Value (Totalizer in m³)',
       'Closing Value (Totalizer in m³)',
       'Water Abstraction (unit in m³)',
     ]);

     // Table rows
     dailyAbstraction.forEach((item) => {
       wsData.push([
         new Date(item.date).toLocaleDateString('en-GB'), // DD-MM-YYYY
         item.openingTotalizer,
         item.closingTotalizer,
         item.dailyConsumption,
       ]);
     });

     // Create workbook and worksheet
     const ws = XLSX.utils.aoa_to_sheet(wsData);
     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

     // Save as Excel file
     XLSX.writeFile(
       wb,
      'Daily_Abstraction_Report_Plant.xlsx',
     );
   };

  const downloadExcelForBorewellConsumption = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = new Date().toLocaleString();

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push([
      'Date',
      'Product ID',
      'Mode',
      'On Time',
      'Off Time',
      'Duration',
      'Source',
    ]);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([
        new Date(item.date).toLocaleDateString('en-GB'), // DD-MM-YYYY
        item.pid,
        item.mode,
        formatTimeTo12Hour(item.onTime),
        formatTimeTo12Hour(item.offTime),
        item.duration,
        item.source,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `Borewell_Consumption_Logs_${productId || 'Plant'}.xlsx`,
    );
  };

  const downloadExcelForEneryConsumption = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = new Date().toLocaleString();

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push(['Date', 'Product ID', 'Unit Consumed (KWH)']);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([
        new Date(item.date).toLocaleDateString('en-GB'), // DD-MM-YYYY
        item.pid,
        `${item.unitConsumed}`,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `Borewell_Energy_Consumption_Logs_${productId || 'Plant'}.xlsx`,
    );
  };

  const downloadExcelForAlertLogs = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = new Date().toLocaleString();

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push(['Date', 'Product ID', 'Status', 'Alerts']);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([
        new Date(item.date).toLocaleDateString('en-GB'), // DD-MM-YYYY
        item.pid,
        item.status,
        item.alerts,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(wb, `Borewell_Alert_Logs_${productId || 'Plant'}.xlsx`);
  };

  const fetchDevicList = async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/device/device-list`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        const data = response?.data?.data?.data;
        setDeviceList(data || []);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/auth/user-permissions`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        const data = response?.data?.data;
        setUserPermissions(data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const checkPermissions = (pid) => {
    if (!pid || pid === '') {
      toast({
        title: 'Please select a valid borewell',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setCanMonitor(false);
      return;
    }
    if (userRole === 'user') {
      const filteredDevice = userPermissions?.filter(
        (item) => item.productId === pid,
      );
      if (filteredDevice?.[0].monitor || filteredDevice?.[0].control) {
        setCanMonitor(true);
      } else {
        setCanMonitor(false);
        toast({
          title: `Access to device ${pid} has been denied.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  function formatISODate(isoString) {
    return moment(isoString).format('DD-MM-YYYY');
  }

  function formatISODateTime(isoString) {
    return moment.utc(isoString).format('DD-MM-YYYY h:mm A');
  }

  function formatTimeTo12Hour(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') return '';

    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr || '00';

    let period = 'AM';

    if (hours >= 12) {
      period = 'PM';
      if (hours > 12) hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    const formattedHours = hours.toString().padStart(2, '0');
    return `${formattedHours}:${minutes} ${period}`;
  }

  const fetchBorewellData = async () => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/borewellAbstraction?pid=${selectedPid}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setDailyAbstraction(response.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchPlantData = async () => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/plantAbstraction?start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setDailyAbstraction(response.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    // E.g., fetch('/api/plant-data')
  };

  const fetchOperationLog = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchEnergyConsumption = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchAlertLog = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setStopDate('');
    setSelectedPid('');
    setDailyAbstraction([]);
    setAnalyticsLogs([]);
    setCanMonitor(true);
  };

  // The main handler function
  const handleTabChange = (index) => {
    clearFilters();
    setActiveTab(index);
  };

  const filterAvgVllData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        VLL: item.avg_vll || 0,
      }));

    setAvgVll(filtered);
  };

  const filterCurrentData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        IR: item.current_r || 0,
        IY: item.current_y || 0,
        IB: item.current_b || 0,
      }));

    setCurrent(filtered);
  };

  const filterPowerFactorData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        PFr: item.pf_r || 0,
        PFy: item.pf_y || 0,
        PFb: item.pf_b || 0,
      }));

    setPowerFactor(filtered);
  };

  const filterFrequencyData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        Frequency: item.frequency || 0,
      }));

    setFrequency(filtered);
  };

  const filterVoltageData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        VR: item.voltage_r || 0,
        VY: item.voltage_y || 0,
        VB: item.voltage_b || 0,
      }));

    setVoltage(filtered);
  };

  const filterKwhData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        KWh: item.total_kwh || 0,
      }));

    setKwh(filtered);
  };

  const filterKwData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: moment.utc(item._time).format('HH:mm'), // format after sorting
        KW: item.total_kw || 0,
      }));

    setKw(filtered);
  };

  const handleTabChangeForEneryConsumption = (index) => {
    setactiveTabEnergyConsumption(index);

    switch (index) {
      case 0:
        filterAvgVllData();
        break;

      case 1:
        filterCurrentData();
        break;

      case 2:
        filterPowerFactorData();
        break;

      case 3:
        filterFrequencyData();
        break;

      case 4:
        filterVoltageData();
        break;

      case 5:
        filterKwhData();
        break;

      case 6:
        filterKwData();
        break;

      default:
        console.log('called');
    }
    // clearFilters();
  };

  const fetchEnergyConsumptionReading = async () => {
    const formattedStart = `${startDateEnergyCons}T00:00:00Z`;
    const formattedStop = `${startDateEnergyCons}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/energyConsumptionReading?pid=${selectedPid}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setenergyConsumptionGraph(response.data.data);
      }
    } catch (error) {
      toast({
        title: error?.response?.data?.data
          ? error?.response?.data?.data
          : error?.response?.data?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchDevicList();
  }, []);

  useEffect(() => {
    if (userRole === 'user') {
      fetchUserPermissions();
    }
  }, []);

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const primaryColor = 'rgba(16, 114, 187, 1)';

  return (
    <Box p={6} bg="gray.50" minH="100vh" mt="20px">
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Analytics
      </Text>

      {/* Tabs Section */}
      <Tabs index={activeTab} onChange={handleTabChange} variant="unstyled">
        <TabList
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
          overflow="hidden"
          px={3}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.key}
              px={6}
              py={3}
              fontWeight="semibold"
              fontSize="sm"
              color={activeTab === index ? 'blue.600' : 'gray.600'}
              borderBottom={
                activeTab === index
                  ? '2px solid rgba(0,106,184,1)'
                  : '2px solid transparent'
              }
              _hover={{ color: 'blue.500' }}
              _focus={{ boxShadow: 'none' }}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {/* Borewell Abstraction Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchBorewellData()}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box
                    border="1px solid"
                    borderColor="#e2e8f0"
                    borderRadius="lg"
                  >
                    <Box maxH="400px" overflowY="auto">
                      <Table variant="striped" size="sm">
                        <Thead bg="#f7faff">
                          <Tr>
                            <Th fontSize="12px">Date</Th>
                            <Th fontSize="12px">Well ID</Th>
                            <Th fontSize="12px">Opening Date</Th>
                            <Th fontSize="12px">Totalizer Opening Reading</Th>
                            <Th fontSize="12px">Closing Date</Th>
                            <Th fontSize="12px">Totalizer Closing Reading</Th>
                            <Th fontSize="12px">Daily Consumption</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dailyAbstraction.map((row, i) => (
                            <Tr key={i}>
                              <Td>{formatISODate(row.date)}</Td>
                              <Td>{row.pid}</Td>
                              <Td>{formatISODateTime(row.openingDate)}</Td>
                              <Td>{row.openingTotalizer}</Td>
                              <Td>{formatISODateTime(row.closingDate)}</Td>
                              <Td>{row.closingTotalizer}</Td>
                              <Td>
                                {row.dailyConsumption}m<sup>3</sup>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>

                    {/* Buttons Container */}
                    <Flex mt={0} gap={4} p={4} justifyContent="flex-start">
                      <Button
                        onClick={downloadExcelForBorewellAbstraction}
                        leftIcon={<XlDownload style={{ width: '20px' }} />}
                        borderRadius="md"
                        variant="outline"
                        borderColor="rgba(16, 114, 187, 1)"
                        color="rgba(16, 114, 187, 1)"
                        fontSize="sm"
                      >
                        Download Excel
                      </Button>
                      <Button
                        disabled
                        leftIcon={<PdfDownload style={{ width: '20px' }} />}
                        borderRadius="md"
                        variant="outline"
                        borderColor="rgba(16, 114, 187, 1)"
                        color="rgba(16, 114, 187, 1)"
                        fontSize="sm"
                      >
                        Download PDF
                      </Button>
                    </Flex>
                  </Box>
                )}

              {/* Chart */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box mt={8}>
                    <Text
                      textAlign="center"
                      fontWeight="semibold"
                      mb={2}
                      color="gray.600"
                    >
                      Usage Data (Daily)
                    </Text>

                    <Box
                      ref={chartRef0}
                      height="300px"
                      minW="300"
                      minH="200"
                      bg="white"
                      borderRadius="xl"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={abstractionChartData}>
                          <CartesianGrid strokDasharray="4 4" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="Unit"
                            fill={primaryColor}
                            radius={[6, 6, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    <Flex justify="flex-start" mt={4}>
                      <Button
                        onClick={handleDownloadGraphPDF}
                        bg={primaryColor}
                        fontSize="14px"
                        color="white"
                        rounded="base"
                        _hover={{ bg: '#005a99' }}
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        py={5}
                      >
                        Download Graph
                      </Button>
                    </Flex>
                  </Box>
                )}
            </Box>
          </TabPanel>

          {/* Plant Abstraction Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex align="end">
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchPlantData()}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {dailyAbstraction.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor="#e2e8f0" // A common light border color (Chakra's gray.200)
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowY="auto"
                  >
                    <Table variant="striped" size="sm">
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg="#f7faff">
                        <Tr>
                          <Th fontSize="12px">Date</Th>
                          <Th fontSize="12px">Totalizer Opening Reading</Th>
                          <Th fontSize="12px">Totalizer Closing Reading</Th>
                          <Th fontSize="12px">Daily Consumption</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {dailyAbstraction.map((row, i) => (
                          <Tr key={i}>
                            <Td>{row.date}</Td>
                            <Td>{row.openingTotalizer}</Td>
                            <Td>{row.closingTotalizer}</Td>
                            <Td>
                              {row.dailyConsumption}m<sup>3</sup>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForPlantAbstraction}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      disabled
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box mt={8}>
                    <Text
                      textAlign="center"
                      fontWeight="semibold"
                      mb={2}
                      color="gray.600"
                    >
                      Usage Data (Daily)
                    </Text>
                    <Box
                      ref={chartRef1}
                      height="300px"
                      bg="white"
                      borderRadius="xl"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={abstractionChartData}>
                          <CartesianGrid strokDasharray="4 4" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="Unit"
                            fill={primaryColor}
                            radius={[6, 6, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    <Flex justify="flex-start" mt={4}>
                      <Button
                        onClick={handleDownloadGraphPDF}
                        bg={primaryColor}
                        fontSize="14px"
                        color="white"
                        rounded="base"
                        _hover={{ bg: '#005a99' }}
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        py={5}
                      >
                        Download Graph
                      </Button>
                    </Flex>
                  </Box>
                )}
            </Box>
          </TabPanel>

          {/* Borewell Operation Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchOperationLog('operation')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor="#e2e8f0" // A common light border color (Chakra's gray.200)
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowY="auto"
                  >
                    <Table variant="striped" size="sm">
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg="#f7faff">
                        <Tr>
                          <Th fontSize="12px">Date</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Mode</Th>
                          <Th fontSize="12px">On Time</Th>
                          <Th fontSize="12px">Off Time</Th>
                          <Th fontSize="12px">Duration</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODate(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.mode}</Td>
                            <Td>{formatTimeTo12Hour(row.onTime)}</Td>
                            <Td>{formatTimeTo12Hour(row.offTime)}</Td>
                            <Td>{row.duration}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForBorewellConsumption}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      disabled
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box mt={8}>
                  <Text
                    textAlign="center"
                    fontWeight="semibold"
                    mb={2}
                    color="gray.600"
                  >
                    Usage Data (Daily)
                  </Text>
                  <Box
                    ref={chartRef2}
                    height="300px"
                    bg="white"
                    borderRadius="xl"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={borewellOperationChartData}>
                        <CartesianGrid strokeDasharray="4 4" />
                        <XAxis dataKey="date" />
                        <YAxis
                          tickFormatter={(value) => {
                            const hours = Math.floor(value / 3600);
                            const minutes = Math.floor((value % 3600) / 60);
                            return `${hours}h ${minutes}m`; // shorter format on Y-axis
                          }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => {
                            const hours = Math.floor(value / 3600);
                            const minutes = Math.floor((value % 3600) / 60);
                            const seconds = Math.floor(value % 60);
                            return [
                              `${hours}h ${minutes}m ${seconds}s`,
                              'Duration',
                            ];
                          }}
                        />
                        <Bar
                          dataKey="value"
                          fill={primaryColor}
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Flex justify="flex-start" mt={4}>
                    <Button
                      onClick={handleDownloadGraphPDF}
                      bg={primaryColor}
                      fontSize="14px"
                      color="white"
                      rounded="base"
                      _hover={{ bg: '#005a99' }}
                      leftIcon={<DownloadIcon />}
                      size="sm"
                      py={5}
                    >
                      Download Graph
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Energy Consumption Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchEnergyConsumption('energy_consumption')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor="#e2e8f0" // A common light border color (Chakra's gray.200)
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowY="auto"
                  >
                    <Table variant="striped" size="sm">
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg="#f7faff">
                        <Tr>
                          <Th fontSize="12px">Date</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Unit Consumed (KWH)</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODate(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.unitConsumed}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForEneryConsumption}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      disabled
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              <Box mt={8}>
                {/* Date selector + button */}
                <Flex justify="left" align="center" gap={3}>
                  <Text
                    textAlign="center"
                    fontWeight="semibold"
                    mb={2}
                    color="gray.600"
                  >
                    Graph
                  </Text>
                  <Flex
                    opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                    pointerEvents={
                      canMonitor || userRole !== 'user' ? 'auto' : 'none'
                    }
                    justify="left"
                    align="center"
                    gap={3}
                  >
                    <Input
                      value={startDateEnergyCons}
                      type="date"
                      maxW="200px"
                      size="sm"
                      borderRadius="md"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                      onChange={(e) => setStartDateEnergyCons(e.target.value)}
                    />
                    <Button
                      bg={primaryColor}
                      color="white"
                      fontSize="14px"
                      size="sm"
                      rounded="base"
                      _hover={{ bg: '#005a99' }}
                      onClick={fetchEnergyConsumptionReading}
                    >
                      Submit
                    </Button>
                  </Flex>
                </Flex>
                {Array.isArray(energyConsumptionGraph) &&
                  energyConsumptionGraph.length > 0 && (
                    <Tabs
                      index={activeTabEnergyConsumption}
                      onChange={handleTabChangeForEneryConsumption}
                      variant="unstyled"
                    >
                      <TabList
                        bg="white"
                        // borderRadius="lg"
                        // border="1px solid"
                        // borderColor="gray.200"
                        boxShadow="sm"
                        overflow="hidden"
                        px={1}
                      >
                        {[
                          'VLL',
                          'Current',
                          'Power Factor',
                          'Frequency',
                          'Voltage',
                          'KWH',
                          'KW',
                        ].map((label, index) => (
                          <Tab
                            key={index}
                            px={6}
                            py={3}
                            fontWeight="semibold"
                            fontSize="sm"
                            color={
                              activeTabEnergyConsumption === index
                                ? 'blue.600'
                                : 'gray.600'
                            }
                            borderBottom={
                              activeTabEnergyConsumption === index
                                ? '2px solid rgba(0,106,184,1)'
                                : '2px solid transparent'
                            }
                            borderRadius="none"
                            _hover={{ color: 'blue.500' }}
                            _focus={{ boxShadow: 'none' }}
                          >
                            {label}
                          </Tab>
                        ))}
                      </TabList>
                      <TabPanels>
                        {/* Panel for Vll graph */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef0}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={avg_vll}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis
                                  dataKey="date"
                                  tickFormatter={(tick) => {
                                    return tick; // already formatted as "HH:mm", you can also use moment(tick).format('HH:mm')
                                  }}
                                />
                                <YAxis unit=" v" />{' '}
                                {/* shows V as unit on Y-axis */}
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'VLL',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                <Line
                                  type="monotone" // smooth line
                                  dataKey="VLL"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false} // hide data points if needed
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for current */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef1}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={current}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" amp" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'IR',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'IY',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'IB',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IR"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IY"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IB"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for power factor */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef2}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={powerFactor}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />

                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'PFr',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'PFb',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'PFy',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="PFr"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="PFy"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="PFb"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for frequency */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef3}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={frequency}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" hz" />
                                <Tooltip />

                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'Frequency',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="Frequency"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for voltage */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef4}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={voltage}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" v" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'VR',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'VY',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'VB',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="VR"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="VY"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="VB"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for KWH */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef5}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={kwh}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" kwh" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'KWh',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="KWh"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for KW */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef6}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={kw}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" kw" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'KW',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="KW"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  )}
              </Box>
            </Box>
          </TabPanel>

          {/* Alert Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchAlertLog('alert')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor="#e2e8f0" // A common light border color (Chakra's gray.200)
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowY="auto"
                  >
                    <Table variant="striped" size="sm">
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg="#f7faff">
                        <Tr>
                          <Th fontSize="12px">Date Time</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Status</Th>
                          <Th fontSize="12px">Alerts</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODateTime(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.status}</Td>
                            <Td>{row.alerts}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForAlertLogs}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      disabled
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AnalyticsDashboard;
